import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { createPortal } from 'react-dom';
import { formatDateDMY, formatDateTimeDMY } from '../../utils/dateUtils';
import axios from 'axios';
import { apiUrl } from '../../config/api';
import FullScreenPanel from '../UI/FullScreenPanel';
import { 
  UserIcon, 
  CashIcon, 
  RepaymentIcon, 
  DocumentIcon, 
  WarningIcon,
  InfoIcon,
  CloseIcon
} from '../UI/IconLibrary';
import SignatureField from '../UI/SignatureField';
import LoanEditForm from './LoanEditForm';
import TownInput from '../UI/TownInput';
import DocumentsStrip from '../DocumentsStrip';
import EntityAuditTrail from '../EntityAuditTrail';
import DisplayField, { FieldGroup } from '../UI/DisplayField';
import { LoanSection, LOAN_GROUPS_GRID, PayoutGroup, RepaymentGroup } from './LoanSections';
import LoanClientHeader from '../LoanClientHeader';
import PenaltyBreakdown from '../PenaltyBreakdown';
import DebitOrderCollections from '../DebitOrderCollections';
import CalculatorDeclineBanner, { useLoanCalculator, RiskReasonField, RecalculateCalculation, clientRejectionMessage } from '../CalculatorDecline';
import DocumentFlagsBanner, { useLoanDocumentFlags } from '../DocumentFlagsBanner';
import BureauReportSection from '../affordability/BureauReportSection';
import { useSuggestedTerms, saveQuotedTerms, startRecalculation } from './suggestedTerms';
import RepaymentLink from '../RepaymentLink';
import { useDetailsModal } from '../../context/DetailsModalContext';
import { clickableProps } from '../../utils/clickable';
import { sortRows, makeSortTh, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from '../UI/tableSort';
import { toCurrency } from '../../utils/adminMetrics';

// A pop-up dialog over the page, in the style of the app's confirmations.
const ActionDialog = ({ title, children, onClose, onConfirm, confirmText, confirmClass, busy, error }) => createPortal(
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label={title}>
    <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden w-full max-w-md"
      style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
    >
      <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />
      <div className="app-modal-header px-5 py-3.5 border-b border-indigo-100/60 dark:border-slate-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
          <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
          {title}
        </h3>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="p-1.5 rounded-full text-gray-400 dark:text-slate-400 hover:bg-indigo-100/60 dark:hover:bg-slate-700 hover:text-indigo-600 dark:hover:text-slate-200 transition-colors"
          title="Close"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-5 space-y-4">
        <div>{children}</div>
        {error && (
          <div className="p-2.5 rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
        )}
        <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors font-medium text-sm border border-gray-200 dark:border-slate-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 rounded-lg transition-colors font-medium text-sm shadow-sm disabled:opacity-60 ${confirmClass}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  </div>,
  document.body
);

// The frame of an embedded Loan Details: just its sections (defined once, so
// the screen is not remounted on every render).
const EmbeddedFrame = ({ children }) => <div className="loan-details-embedded">{children}</div>;

// A loan still being decided: its calculator panel is at the top.
const DECIDING_STATUSES = ['Pending', 'Awaiting Staff', 'Awaiting Client', 'Awaiting Approval'];

const LoanDetails = ({
  loan = {},
  onClose = () => {},
  canApprove = false,
  canReject = false,
  canRevertToPending = false,
  approveLoan = () => {},
  handleShowRejectionForm = () => {},
  moreDetailsNotes = {},
  setMoreDetailsNotes = () => {},
  showNoteInput = {},
  setShowNoteInput = () => {},
  userRole = '',
  onSavedNote = () => {},
  onApproved = () => {},
  // After the loan was edited from here (Edit Loan): the page reloads its loans.
  onEdited = () => {},
  // Extra controls for the header bar (e.g. "Open full page" in the quick look).
  headerExtra = null,
  // The contract to sign as lender and witness, for a loan Awaiting Staff
  // (the Dashboard's review). Every loan opens in this one screen.
  staffSigning = null,
  // embedded: the same sections without the full-screen frame, inside another
  // screen (the client's agreement review shows the loan this way above the
  // contract, so it is the same Loan Details everywhere).
  embedded = false,
  // Shown at the bottom of the Documents section, where the application
  // form has its photo step (the client's review puts the live photo there).
  inDocuments = null,
}) => {
  // A repayment row opens its quick view (staff, or the client whose loan it is).
  const { openRepayment, canViewRepayments } = useDetailsModal();
  // The audit trail is for staff only; a client never sees it.
  const { user: authUser } = useContext(AuthContext) || {};
  const authPerms = authUser?.permissions || [];
  const canSeeAuditTrail = ['*', 'ViewLoans', 'ManageLoans', 'ManageClients', 'AuditTrail', 'Audits', 'ManageConfig'].some((p) => authPerms.includes(p))
    && String(userRole).toLowerCase() !== 'client';
  const [documents, setDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [errorDocs, setErrorDocs] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [additionalExpenses, setAdditionalExpenses] = useState([]);
  const [additionalExpensesTotal, setAdditionalExpensesTotal] = useState(0);
  const [loadingAdditionalExpenses, setLoadingAdditionalExpenses] = useState(false);
  // The expenses have been read for this loan (the suggested terms are quoted with them).
  const [expensesReady, setExpensesReady] = useState(false);
  const [rejectInputOpen, setRejectInputOpen] = useState(false);
  // The resend-note and reject dialogs: their errors and whether they are saving.
  const [noteError, setNoteError] = useState('');
  const [dialogSaving, setDialogSaving] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  // Approving a loan the affordability check declined or referred: the
  // reason it is approved (kept with the check as the override).
  const [approveReasonOpen, setApproveReasonOpen] = useState(false);
  const [approveReason, setApproveReason] = useState('');
  const [approveReasonError, setApproveReasonError] = useState('');
  // What holds the loan, as the API said it (a pre-qualification without a
  // bureau report, or only the client's own report); '' for the decision.
  const [approveHold, setApproveHold] = useState('');
  const [cancellationNotes, setCancellationNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [statusNotes, setStatusNotes] = useState([]);
  const [loadingStatusNotes, setLoadingStatusNotes] = useState(false);
  // Approval prerequisites (final signatures) state
  const [signatures, setSignatures] = useState(null);
  const [loadingSigs, setLoadingSigs] = useState(false);
  const [sigErrorMsg, setSigErrorMsg] = useState('');
  const [approveError, setApproveError] = useState('');
  // Local capture buffers for final approval stage
  const [lenderSignature, setLenderSignature] = useState(null);
  const [lenderInitials, setLenderInitials] = useState(null);
  const [witness2Signature, setWitness2Signature] = useState(null);
  const [witness2Initials, setWitness2Initials] = useState(null);
  const [lenderLocation, setLenderLocation] = useState('Windhoek');
  // A document opened from Documents shows across the full width, below the
  // credit score and documents.
  const [docViewerEl, setDocViewerEl] = useState(null);
  // Repayments: two per page, newest first, optionally within a date range.
  const REPAYMENTS_PER_PAGE = 2;
  const [repPage, setRepPage] = useState(1);
  // A click on a repayments column sorts them all (newest first until then).
  const [repSort, setRepSort] = useState(null);
  const [repFrom, setRepFrom] = useState('');
  const [repTo, setRepTo] = useState('');
  // The calculator's verdict (staff only); a loan it declined needs the
  // assessor's reason to be sent back to the client.
  const [calculatorRun, setCalculatorRun] = useState(0);
  // Bumped when a document is added here (the bureau report PDF).
  const [docsReload, setDocsReload] = useState(0);
  const calculator = useLoanCalculator(loan, calculatorRun);
  // Still being decided: the calculator sits at the top of the loan.
  const calculatorOnTop = DECIDING_STATUSES.includes(loan?.status);
  const documentFlags = useLoanDocumentFlags(loan);
  const calculatorDeclined = !!calculator?.declined;
  const [riskReason, setRiskReason] = useState('');
  // Accepting an application the calculator declined: it came in unsigned
  // for review (Awaiting Staff); staff set the amount and term they are
  // prepared to lend and send it to the client to sign (Awaiting Client).
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ amount: '', months: '', note: '' });
  const [acceptError, setAcceptError] = useState('');

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!loan?.id) return;
      setLoadingDocs(true);
      setErrorDocs(null);
      try {
        const token = localStorage.getItem('token');
        const resolvedClientId = loan?.client_id ?? loan?.clientId ?? loan?.client?.id ?? loan?.client?.client_id ?? null;
        // Fetch loan documents and client's identification documents in parallel
        const [loanDocsRes, clientDocsRes, clientIdDocsRes] = await Promise.all([
          axios.get(apiUrl(`/api/document/loan/${loan.id}`), {
            headers: { Authorization: `Bearer ${token}` }
          }),
          resolvedClientId
            ? axios.get(apiUrl(`/api/document/client/${resolvedClientId}`), {
                headers: { Authorization: `Bearer ${token}` }
              })
            : Promise.resolve({ data: [] }),
          resolvedClientId
            ? axios.get(apiUrl(`/api/document/client-id/${resolvedClientId}`), {
                headers: { Authorization: `Bearer ${token}` }
              })
            : Promise.resolve({ data: [] })
        ]);

        // Handle the loan documents response format correctly
        const loanDocs = (loanDocsRes.data?.documents || (Array.isArray(loanDocsRes.data) ? loanDocsRes.data : [])).map(doc => ({
          ...doc,
          document_scope: 'loan'
        }));
        const rawClientDocs = [
          ...(Array.isArray(clientDocsRes.data) ? clientDocsRes.data : []),
          ...(Array.isArray(clientIdDocsRes.data) ? clientIdDocsRes.data : [])
        ];

        // Filter client documents to include any ID/identification/passport variants
        const filteredClientDocs = rawClientDocs.filter(d => {
          const type = (d?.document_type || '').toLowerCase();
          return type.includes('id') || type.includes('identification') || type.includes('passport');
        });

        // Deduplicate by document id to avoid showing the same ID twice when both endpoints return it
        const seenDocIds = new Set();
        const clientIdDocs = filteredClientDocs.map(doc => ({
          ...doc,
          document_scope: 'client-id'
        })).filter(doc => {
          if (!doc?.id) return true;
          if (seenDocIds.has(doc.id)) return false;
          seenDocIds.add(doc.id);
          return true;
        });

        setDocuments([...clientIdDocs, ...loanDocs]);
      } catch (err) {
        setErrorDocs('Failed to fetch documents');
      }
      setLoadingDocs(false);
    };
    fetchDocuments();
  }, [loan?.id, loan?.client_id, loan?.clientId, loan?.client?.id, loan?.client?.client_id, docsReload]);

  // Fetch invoices so we can display the penalty interest (from the latest invoice)
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!loan?.id) {
        setInvoices([]);
        return;
      }
      setLoadingInvoices(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/invoices`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(Array.isArray(res.data) ? res.data : []);
      } catch {
        setInvoices([]);
      }
      setLoadingInvoices(false);
    };
    fetchInvoices();
  }, [loan?.id]);

  // Fetch additional loan expenses (NAMFISA levy, duty stamp, etc.)
  useEffect(() => {
    const fetchAdditionalExpenses = async () => {
      if (!loan?.id) {
        setAdditionalExpenses([]);
        setAdditionalExpensesTotal(0);
        return;
      }
      setLoadingAdditionalExpenses(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/additional-expenses`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Whatever the loan actually carries, including nothing at all. An
        // expense that was removed must not reappear in the review.
        const expenses = Array.isArray(res.data?.expenses) ? res.data.expenses : [];
        const total = Number(res.data?.total) || 0;
        setAdditionalExpenses(expenses);
        setAdditionalExpensesTotal(total);
      } catch {
        setAdditionalExpenses([]);
        setAdditionalExpensesTotal(0);
      }
      setLoadingAdditionalExpenses(false);
      setExpensesReady(true);
    };

    fetchAdditionalExpenses();
  }, [loan?.id, loan?.principal_amount, loan?.principal_amount_plus_interest]);

  // Penalty interest month by month, as the API charged it (GN 263/2018),
  // from the invoices already fetched; undefined when they do not carry it
  // (PenaltyBreakdown then fetches it for the loan).
  const penaltyBreakdown = React.useMemo(() => {
    const latest = Array.isArray(invoices) && invoices.length ? invoices[0] : null;
    return latest && latest.penalty_breakdown !== undefined ? latest.penalty_breakdown : undefined;
  }, [invoices]);
  const penaltyMonthsCharged = Array.isArray(penaltyBreakdown?.months) ? penaltyBreakdown.months.length : 0;

  const principalPlusInterest = Number(loan?.principal_amount_plus_interest || 0);
  // What staff may do with the loan is the API's call (loan.state.staff_actions:
  // its status and this user's permissions); a loan without it offers nothing.
  const staffActions = loan?.state?.staff_actions || {};
  const canSendBack = staffActions.send_back === true;
  // Edit Loan in the header: a loan being reviewed or awaiting approval, when
  // the API lets this user edit it. It opens the shared edit form.
  const canEditHere = staffActions.edit === true && ['Awaiting Staff', 'Awaiting Approval'].includes(loan?.status);
  const [editing, setEditing] = useState(false);
  // The approval panel shows when the parent lets this user approve or reject.
  const showApprovalActions = Boolean(canApprove || canReject);
  // Show the repayments section where a payment can still be taken, or where
  // one already was: a loan that is paid off, terminated or written off keeps
  // its history, so the test is not on status alone.
  const showRepayments =
    staffActions.enter_repayment === true ||
    loan?.state?.actions?.repay === true ||
    (Array.isArray(loan?.repayments) && loan.repayments.length > 0);

  // What was paid, as the API counts it (loan.state); a loan sent without its
  // state falls back to adding up the repayments listed, for display only.
  const totalPaid = loan?.state && loan.state.total_paid !== undefined
    ? Number(loan.state.total_paid) || 0
    : (Array.isArray(loan?.repayments)
      ? loan.repayments.reduce((sum, repayment) => sum + Number(repayment?.amount_paid || 0), 0)
      : 0);
  const penaltyInterest = Number(loan?.penalty_interest || 0);
  // When the loan became bad debt: its first bad debt note, shown once in the
  // loan details however many were recorded.
  const badDebtNote = (statusNotes || []).filter((n) => n.note_type === 'BadDebt').slice(-1)[0] || null;
  // Penalties only arise once a loan is paid out and falls overdue, so the
  // history shows only then (or when there already are penalties).
  // Overdue as the API sees it (loan.state: only a loan being repaid can be).
  const pastDue = loan?.state?.is_overdue === true;
  const showPenalties =
    penaltyMonthsCharged > 0 ||
    penaltyInterest > 0 ||
    ['Bad Debt', 'Written off'].includes(loan?.status) ||
    pastDue;
  const baseLoanObligation = Number((principalPlusInterest + additionalExpensesTotal).toFixed(2));

  // The calculator's suggested amount (the loan asks for more than the client
  // qualifies for): filled into Loan Details with every figure worked out
  // for it, for staff who may change the loan. Update saves it (a signed
  // loan goes back to the client to sign) and runs the calculation again;
  // "Keep" shows the loan as it is.
  const [keepCurrentTerms, setKeepCurrentTerms] = useState(false);
  const [updatingTerms, setUpdatingTerms] = useState(false);
  const [termsMessage, setTermsMessage] = useState('');
  const [termsError, setTermsError] = useState('');
  const suggested = useSuggestedTerms({
    loan,
    calculator,
    expenseCodes: expensesReady ? additionalExpenses.map((e) => e.expense_code).filter(Boolean) : null,
    enabled: staffActions.edit === true && !embedded && !keepCurrentTerms,
  });
  const q = suggested.quote;
  const updateToSuggested = async () => {
    if (!q) return;
    setUpdatingTerms(true);
    setTermsError('');
    try {
      const updated = await saveQuotedTerms(loan, q);
      // The affordability check tests the new instalment.
      const rerun = loan?.credit_assessment_id ? await startRecalculation(loan.id) : null;
      const backToClient = updated?.status && updated.status !== loan?.status;
      const term = `${q.duration_months} ${q.duration_months === 1 ? 'month' : 'months'}`;
      setTermsMessage(`Loan updated to ${toCurrency(q.principal_amount)} over ${term}.`
        + (backToClient ? ` It has gone back for the client's signature (${updated.status}).` : ' The client signs the new agreement.')
        + (rerun ? ' The calculation is being run again.' : ''));
      setTimeout(() => {
        onClose();
        try { onEdited(); } catch {}
      }, 1800);
    } catch (e) {
      setTermsError(e?.response?.data?.error || 'The loan could not be updated.');
    }
    setUpdatingTerms(false);
  };
  // The figure shown, and the loan's own one struck through next to it while
  // the suggestion is filled in.
  const was = (now, before) => (q && String(now) !== String(before)
    ? <>{now} <span className="ml-1 text-[11px] text-gray-400 dark:text-slate-500 line-through">{before}</span></>
    : now);
  const dmyOf = (v) => (v ? formatDateDMY(v) : '-');
  // What is still owed, penalties included: the API's figure, never redone here.
  const effectiveTotalDue = loan?.total_due !== undefined && loan?.total_due !== null
    ? Number(loan.total_due)
    : NaN;

  // Fetch collected signatures to enforce final approval requirements
  useEffect(() => {
    const fetchSigs = async () => {
      if (!loan?.id) return;
      setLoadingSigs(true);
      setSigErrorMsg('');
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/signatures`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSignatures(res.data);
        // Initialize local buffers from existing signatures
        const s = res.data || {};
        if (s.lender_signature) setLenderSignature(s.lender_signature);
        if (s.lender_initials) setLenderInitials(s.lender_initials);
        if (s.witness2_signature) setWitness2Signature(s.witness2_signature);
        if (s.witness2_initials) setWitness2Initials(s.witness2_initials);
        try {
          const locs = s.signature_locations && (typeof s.signature_locations === 'string' ? JSON.parse(s.signature_locations) : s.signature_locations);
          if (locs?.lenderLocation) setLenderLocation(locs.lenderLocation);
        } catch {}
      } catch (e) {
        setSigErrorMsg('Failed to load signatures');
        setSignatures(null);
      }
      setLoadingSigs(false);
    };
    // Always try to load, but it's especially needed for Awaiting Approval
    fetchSigs();
  }, [loan?.id]);

  // Compute missing final signatures/initials for approval gate
  const missingFinalSigs = React.useMemo(() => {
    const missing = [];
    // The fields' values: loaded from what was saved, and cleared by the
    // field when a saved one is blank (SignatureField), so a blank counts as
    // missing.
    const effLenderSig = lenderSignature;
    const effLenderInit = lenderInitials;
    const effW2Sig = witness2Signature;
    const effW2Init = witness2Initials;
    if (!effLenderSig) missing.push('Lender signature');
    if (!effLenderInit) missing.push('Lender initials');
    if (!effW2Sig) missing.push('Witness 2 signature');
    if (!effW2Init) missing.push('Witness 2 initials');
    return missing;
  }, [signatures, lenderSignature, lenderInitials, witness2Signature, witness2Initials]);

  const requiresFinalSigs = loan?.status === 'Awaiting Approval';
  // A saved lender or witness signature can be cleared only by the person who
  // signed it (lender_signed_by / witness2_signed_by); for one saved before
  // that was kept, nobody knows, so nobody clears it.
  const savedBySomeoneElse = (saved, signer) => Boolean(saved) && (signer == null || Number(signer) !== Number(authUser?.id));
  const lenderLocked = savedBySomeoneElse(signatures?.lender_signature || signatures?.lender_initials, signatures?.lender_signed_by);
  const witness2Locked = savedBySomeoneElse(signatures?.witness2_signature || signatures?.witness2_initials, signatures?.witness2_signed_by);
  const canApproveNow = !requiresFinalSigs || (missingFinalSigs.length === 0);

  // Fetch cancellation notes when loan is Cancelled
  useEffect(() => {
    const fetchCancellationNotes = async () => {
      if (!loan?.id || loan?.status !== 'Cancelled') {
        setCancellationNotes([]);
        return;
      }
      setLoadingNotes(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/notes?type=Cancellation`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCancellationNotes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setCancellationNotes([]);
      }
      setLoadingNotes(false);
    };
    fetchCancellationNotes();
  }, [loan?.id, loan?.status]);

  // Fetch Bad Debt / Written off notes
  useEffect(() => {
    const fetchStatusNotes = async () => {
      if (!loan?.id) {
        setStatusNotes([]);
        return;
      }

      const st = String(loan?.status || '').trim();
      const typeParam = st === 'Bad Debt'
        ? 'BadDebt'
        : (st === 'Written off' ? 'BadDebt,WriteOff' : '');

      if (!typeParam) {
        setStatusNotes([]);
        return;
      }

      setLoadingStatusNotes(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}/notes?type=${encodeURIComponent(typeParam)}`), {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStatusNotes(Array.isArray(res.data) ? res.data : []);
      } catch {
        setStatusNotes([]);
      }
      setLoadingStatusNotes(false);
    };

    fetchStatusNotes();
  }, [loan?.id, loan?.status]);

  // Document viewing, downloading and thumbnails all live in DocumentsStrip now.

  const closeNote = () => {
    setShowNoteInput(prev => ({ ...prev, [loan?.id]: false }));
    setMoreDetailsNotes(prev => { const next = { ...prev }; delete next[loan?.id]; return next; });
    setNoteError('');
  };
  const sendNote = async () => {
    const note = (moreDetailsNotes?.[loan?.id] || '').trim();
    if (!note) { setNoteError('Write what the client must correct or add.'); return; }
    if (calculatorDeclined && !riskReason.trim()) { setNoteError('Write why you are prepared to go ahead.'); return; }
    setDialogSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/loans/${loan?.id}/staff-review`),
        {
          note_text: note,
          note_type: 'Client Message',
          is_internal: false,
          metadata: { source: 'Resend application form' },
          ...(calculatorDeclined ? { risk_reason: riskReason.trim() } : {})
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeNote();
      if (typeof onSavedNote === 'function') onSavedNote();
      onClose();
    } catch (e) {
      setNoteError(e?.response?.data?.error || 'Failed to send the note to the client.');
    } finally {
      setDialogSaving(false);
    }
  };
  // The calculator declined and the client asked for review: the loan waits
  // with staff, unsigned (an online application). Accepting it sends it to
  // the client to sign; the same API step as sending it back (staff-review),
  // with the amount and term staff are prepared to lend and their reason.
  const canAcceptForSigning = canSendBack && loan?.status === 'Awaiting Staff' && calculatorDeclined
    && (loan?.application_type || 'Online') === 'Online' && !loadingSigs && !signatures?.borrower_signature;
  const openAccept = () => {
    setRejectInputOpen(false);
    setShowNoteInput(prev => ({ ...prev, [loan?.id]: false }));
    const suggestion = calculator?.suggestion || null;
    setAcceptForm({
      amount: String(suggestion?.amount || loan?.principal_amount || ''),
      months: String(suggestion?.months || loan?.duration_months || ''),
      note: 'Your application has been accepted. Please review the loan agreement and sign it to continue.',
    });
    setAcceptError('');
    setAcceptOpen(true);
  };
  const confirmAccept = async () => {
    const amount = Number(acceptForm.amount);
    const months = parseInt(acceptForm.months, 10);
    if (!Number.isFinite(amount) || amount <= 0) { setAcceptError('Enter the amount you are prepared to lend.'); return; }
    if (!Number.isFinite(months) || months <= 0) { setAcceptError('Enter the repayment term in months.'); return; }
    if (!acceptForm.note.trim()) { setAcceptError('Write the note the client receives.'); return; }
    if (!riskReason.trim()) { setAcceptError('Write why you are prepared to go ahead.'); return; }
    setDialogSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/loans/${loan?.id}/staff-review`),
        {
          principal_amount: amount,
          duration_months: months,
          note_text: acceptForm.note.trim(),
          note_type: 'Client Message',
          is_internal: false,
          metadata: { source: 'Accepted for signing' },
          risk_reason: riskReason.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAcceptOpen(false);
      if (typeof onSavedNote === 'function') onSavedNote();
      onClose();
    } catch (e) {
      setAcceptError(e?.response?.data?.error || 'Failed to accept the application.');
    } finally {
      setDialogSaving(false);
    }
  };
  const closeReject = () => { setRejectInputOpen(false); setRejectReason(''); setRejectError(''); };
  // The rejection message, written from the calculator when the loan has a
  // result: the dialog opens with it, and staff may change it. Without one,
  // the reason is typed.
  const calculatorRejection = clientRejectionMessage(calculator, loan);
  const confirmReject = async () => {
    const reason = (rejectReason || '').trim();
    if (!reason) { setRejectError('Rejection reason is required'); return; }
    setDialogSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        apiUrl(`/api/loans/${loan?.id}/reject`),
        { rejection_reason: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      closeReject();
      onSavedNote && onSavedNote();
      onClose();
    } catch (e) {
      setRejectError(e?.response?.data?.error || 'Failed to reject loan');
    } finally {
      setDialogSaving(false);
    }
  };

  // The affordability check's decision in force on the loan: a Declined or
  // Manual-review loan is approved with the reason (the API records it).
  const affordabilityDecision = loan?.affordability_decision || calculator?.affordability_decision || null;
  const approveNeedsReason = ['Declined', 'ManualReview'].includes(affordabilityDecision);
  // Approve, with the reason when the check did not approve it.
  const runApprove = async (reason = '') => {
        try {
          if (requiresFinalSigs) {
            const token = localStorage.getItem('token');
            await axios.post(
              apiUrl(`/api/loans/${loan.id}/staff-finalize`),
              {
                lenderSignature,
                witness2Signature,
                initials: {
                  lenderInitials,
                  witness2Initials,
                },
                signatureLocations: {
                  ...(signatures?.signature_locations && (typeof signatures.signature_locations === 'string' ? JSON.parse(signatures.signature_locations) : signatures.signature_locations)),
                  lenderLocation: lenderLocation || 'Windhoek'
                }
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          }
          await approveLoan(loan?.id, reason ? { approve_reason: reason } : {});
          setApproveReasonOpen(false);
          onClose();
          try { onApproved(); } catch {}
        } catch (err) {
          const msg = err?.response?.data?.error || err?.message || 'Failed to submit final signatures';
          // The check's decision changed since the loan was opened: ask for the reason.
          if (err?.response?.data?.code === 'APPROVE_REASON_REQUIRED' && !approveReasonOpen) {
            setApproveReason('');
            setApproveHold(err?.response?.data?.hold || '');
            setApproveReasonError('');
            setApproveReasonOpen(true);
          } else if (approveReasonOpen) {
            setApproveReasonError(msg);
          } else {
            setApproveError(msg);
          }
        }
  };
  const confirmApproveReason = async () => {
    const reason = approveReason.trim();
    if (!reason) { setApproveReasonError('Write the reason you approve this loan.'); return; }
    setDialogSaving(true);
    await runApprove(reason);
    setDialogSaving(false);
  };

  // Approve / Reject / Resend, in the top bar beside the client's name.
  const approvalButtons = showApprovalActions && (canApprove || canReject) ? (
    <div className="flex items-center gap-2 flex-shrink-0">
      {canApprove && (
        <button
          onClick={() => {
            setApproveError('');
            if (requiresFinalSigs && !canApproveNow) {
              setApproveError('Final approval requires: ' + missingFinalSigs.join(', '));
              return;
            }
            if (approveNeedsReason) {
              setApproveReason('');
              setApproveHold('');
              setApproveReasonError('');
              setApproveReasonOpen(true);
              return;
            }
            runApprove();
          }}
          disabled={!canApproveNow || loadingSigs}
          title={requiresFinalSigs && !canApproveNow ? `Missing: ${missingFinalSigs.join(', ')}` : ''}
          className={`px-4 py-1.5 rounded-md text-white text-sm font-medium transition-colors ${(!canApproveNow || loadingSigs) ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
        >
          Approve Loan
        </button>
      )}
      {canReject && (
        <button
          onClick={() => { setShowNoteInput(prev => ({ ...prev, [loan?.id]: false })); setRejectReason(calculatorRejection || ''); setRejectError(''); setRejectInputOpen(true); }}
          className="px-4 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium transition-colors"
        >
          Reject Loan
        </button>
      )}
      {canAcceptForSigning && (
        <button
          onClick={openAccept}
          className="px-4 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
        >
          Accept for signing
        </button>
      )}
      {canSendBack && (
        <button
          onClick={() => { setRejectInputOpen(false); setNoteError(''); setShowNoteInput(prev => ({ ...prev, [loan?.id]: true })); }}
          className="px-4 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium transition-colors"
        >
          Resend application form
        </button>
      )}
    </div>
  ) : null;

  const Frame = embedded ? EmbeddedFrame : FullScreenPanel;
  return (
    <>
    <Frame
      isOpen
      onClose={onClose}
      title="Loan Details"
      subtitle={loan?.loan_code || (loan?.id ? `Loan #${loan.id}` : '')}
      headerRight={
        <div className="flex items-center gap-4 min-w-0">
          {approvalButtons}
          {canEditHere && !embedded && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="flex-shrink-0 px-4 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium transition-colors"
            >
              Edit Loan
            </button>
          )}
          <RecalculateCalculation compact loan={loan} calculator={calculator} onDone={() => setCalculatorRun((n) => n + 1)} />
          <LoanClientHeader loan={loan} />
          {headerExtra}
          {canAcceptForSigning && !showApprovalActions && (
            <button
              type="button"
              onClick={openAccept}
              className="flex-shrink-0 px-4 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm font-medium transition-colors"
            >
              Accept for signing
            </button>
          )}
          {canSendBack && !showApprovalActions && (
            <button
              type="button"
              onClick={() => { setRejectInputOpen(false); setNoteError(''); setShowNoteInput(prev => ({ ...prev, [loan?.id]: true })); }}
              className="flex-shrink-0 px-4 py-1.5 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm font-medium transition-colors"
            >
              Resend application form
            </button>
          )}
        </div>
      }
    >
      {/* The calculator's panel: at the top while the loan is still being
          decided, otherwise as the Affordability section below. One place
          at a time, the same component in both. */}
      {calculatorOnTop && (
        <CalculatorDeclineBanner calculator={calculator} loan={loan} className="mb-4" />
      )}
      <DocumentFlagsBanner flags={documentFlags} className="mb-4" />
      {/* Sections in rows: two side by side on wide screens, the wide ones
          (documents, repayments, audit trail) across the whole row. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start [&>*]:!m-0" style={{ gridAutoFlow: 'row dense' }}>

          {/* Actions (from loan.state.staff_actions):
             - approve/reject allowed: Approve/Reject/Resend application form to client
             - only send back allowed: "Resend application form" in the header
           */}
          {showApprovalActions && (
            <>
              {(canApprove || canReject) ? (
                // The buttons are in the top bar (the note and the reject reason
                // open as dialogs); this box holds the final signatures.
                (requiresFinalSigs || approveError) ? (
                <div className="order-1 xl:col-span-2 mb-6 bg-yellow-50/50 dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-yellow-100 dark:border-slate-700">
                  <div className="flex items-center mb-4 pb-2 border-b border-yellow-100 dark:border-slate-700">
                    <InfoIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                    <h3 className="text-base font-medium text-yellow-800 dark:text-yellow-200">Loan Approval Actions</h3>
                  </div>
                  
                  {/* Final approval requirements + capture */}
                  {requiresFinalSigs && (
                    <div className="mt-3">
                      <div className="text-sm mb-3">
                        {loadingSigs ? (
                          <div className="text-gray-600 dark:text-slate-300">Checking final signatures…</div>
                        ) : sigErrorMsg ? (
                          <div className="text-red-600 dark:text-red-400">{sigErrorMsg}</div>
                        ) : missingFinalSigs.length > 0 ? (
                          <div className="text-red-700 dark:text-red-300">
                            Final approval requires the following to be captured: {missingFinalSigs.join(', ')}.
                          </div>
                        ) : (
                          <div className="text-green-700 dark:text-green-300">All final signatures and initials are present. You can approve.</div>
                        )}
                        {approveError && (
                          <div className="mt-1 text-red-700 dark:text-red-300">{approveError}</div>
                        )}
                      </div>

                      {/* Capture UI for Lender & Witness 2 */}
                      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded p-3">
                        <div className="mb-3 text-sm text-gray-700 dark:text-slate-300">Final Signatures (captured at approval)</div>
                        <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-slate-50 dark:bg-slate-700/60 border border-slate-200 dark:border-slate-600 px-4 py-3 text-sm">
                          <span className="font-medium text-slate-700 dark:text-slate-200">Signed at</span>
                          <TownInput className="w-60" value={lenderLocation} onChange={setLenderLocation} />
                        </div>

                        {/* The lender, then witness 2: each signature with its
                            initials beside it, wrapping when the panel is narrow. */}
                        {/* The borrower, the lender and witness 2: all six in one row
                            across the panel (compact boxes), wrapping only on a
                            narrow screen. */}
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <SignatureField compact label="Borrower's signature" value={signatures?.borrower_signature || null} disabled />
                          <SignatureField compact label="Borrower's initials" kind="initials" value={signatures?.borrower_initials || null} disabled />
                          <SignatureField compact label="Lender's signature" value={lenderSignature} onChange={setLenderSignature} locked={lenderLocked} />
                          <SignatureField compact label="Lender's initials" kind="initials" value={lenderInitials} onChange={setLenderInitials} locked={lenderLocked} />
                          <SignatureField compact label="Witness 2 signature" value={witness2Signature} onChange={setWitness2Signature} locked={witness2Locked} />
                          <SignatureField compact label="Witness 2 initials" kind="initials" value={witness2Initials} onChange={setWitness2Initials} locked={witness2Locked} />
                        </div>
                      </div>
                    </div>
                  )}

                </div>
                ) : null
              ) : (
                // Staff: Only show the note if it exists
                moreDetailsNotes?.[loan?.id] && (
                  <div className="order-1 xl:row-span-2 mb-6 bg-yellow-50/50 dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-yellow-100 dark:border-slate-700">
                    <div className="flex items-center mb-3 pb-2 border-b border-yellow-100 dark:border-slate-700">
                      <WarningIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                      <h3 className="text-base font-medium text-yellow-800 dark:text-yellow-200">Requested Details Note</h3>
                    </div>
                    <div className="whitespace-pre-line text-gray-700 dark:text-slate-300 text-sm">{moreDetailsNotes?.[loan?.id]}</div>
                  </div>
                )
              )}
            </>
          )}

          {/* Affordability: the same calculator panel as at the top (one
              component), shown here once the top one is gone. */}
          {!calculatorOnTop && (
          <section className="order-2 mb-6">
            <h3 className="mb-2 text-base font-medium text-indigo-900 dark:text-indigo-200">Affordability (Microlending Act)</h3>
            {calculator?.summary || calculator?.declined
              ? <CalculatorDeclineBanner calculator={calculator} loan={loan} />
              : (
                <p className="border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3 text-sm text-gray-600 dark:text-slate-300">
                  {loan?.credit_assessment_id
                    ? 'Loading the affordability calculation…'
                    : "No affordability calculation is saved on this loan: it was captured without a pre-qualification, or before calculations were kept on loans. Check the client's documents before approving."}
                </p>
              )}
          </section>
          )}

          {/* The credit bureau report (s 24(4)): captured here, read by the
              affordability check when the calculation is run again. */}
          <BureauReportSection
            loan={loan}
            calculator={calculator}
            className="order-2 mb-6 xl:col-span-2"
            onChanged={() => { setCalculatorRun((n) => n + 1); setDocsReload((n) => n + 1); }}
          />

          {/* Documents — between the approval actions and the client's details,
              in one row with the viewer opening underneath. */}
          <LoanSection Icon={DocumentIcon} title="Documents" className={`order-3 mb-6 ${calculatorOnTop ? 'xl:col-span-2' : ''}`}>
            <DocumentsStrip
              viewerTarget={docViewerEl}
              documents={documents}
              loading={loadingDocs}
              error={errorDocs}
              emptyMessage="No documents found for this loan."
              badgeFor={(doc) => (doc.document_scope === 'loan'
                ? { label: `Loan ${loan?.loan_code || loan?.id || ''}`.trim() }
                : { label: 'Client', tone: 'muted' })}
            />
            {inDocuments && <div className="mt-4 empty:hidden">{inDocuments}</div>}
          </LoanSection>

          {/* Repayments — directly under the client, and only where money can
              move or already has. A loan still awaiting signatures, or one
              rejected or cancelled, never takes a payment, so the section
              would say "none" and mean nothing. */}
          {showRepayments && (
            <div className="order-4 xl:col-span-2 mt-6 bg-white dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-gray-100 dark:border-slate-700">
              <div className="flex items-center mb-4 pb-2 border-b border-gray-100 dark:border-slate-700">
                <RepaymentIcon className="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
                <h3 className="text-base font-medium text-gray-800 dark:text-slate-100">Repayments</h3>
              </div>

              {/* One row per payment, in the same table look as the expenses;
                  two at a time, newest first, with a date filter. */}
              {(() => {
                const all = Array.isArray(loan?.repayments) ? loan.repayments : [];
                if (!all.length) {
                  return <div className="text-center py-4 text-sm text-gray-500 dark:text-slate-400">No repayments yet.</div>;
                }
                const dayOf = (r) => String(r?.date || r?.payment_date || '').slice(0, 10);
                const filtered = sortRows(all
                  .filter((r) => (!repFrom || dayOf(r) >= repFrom) && (!repTo || dayOf(r) <= repTo))
                  .sort((x, y) => dayOf(y).localeCompare(dayOf(x))), repSort);
                const repTh = makeSortTh(repSort, setRepSort);
                const repHead = `px-3 py-2 ${TABLE_HEAD_TEXT}`;
                const pages = Math.max(1, Math.ceil(filtered.length / REPAYMENTS_PER_PAGE));
                const page = Math.min(repPage, pages);
                const rows = filtered.slice((page - 1) * REPAYMENTS_PER_PAGE, page * REPAYMENTS_PER_PAGE);
                const filtering = Boolean(repFrom || repTo);
                const shownTotal = filtered.reduce((sum, r) => sum + Number(r?.amount ?? r?.amount_paid ?? 0), 0);
                const pagerBtn = 'px-2 py-0.5 rounded border border-gray-200 dark:border-slate-600 text-xs text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40';
                return (
                  <>
                    <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                      <label className="text-gray-500 dark:text-slate-400" htmlFor="rep-from">From</label>
                      <input id="rep-from" type="date" value={repFrom} max={repTo || undefined}
                        onChange={(e) => { setRepFrom(e.target.value); setRepPage(1); }} className="w-36" />
                      <label className="text-gray-500 dark:text-slate-400" htmlFor="rep-to">To</label>
                      <input id="rep-to" type="date" value={repTo} min={repFrom || undefined}
                        onChange={(e) => { setRepTo(e.target.value); setRepPage(1); }} className="w-36" />
                      {filtering && (
                        <button type="button" onClick={() => { setRepFrom(''); setRepTo(''); setRepPage(1); }}
                          className="text-blue-600 dark:text-blue-300 hover:underline">Clear</button>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <button type="button" className={pagerBtn} disabled={page <= 1} onClick={() => setRepPage(page - 1)} aria-label="Previous page">‹</button>
                        <span className="px-1 text-gray-600 dark:text-slate-300">{page} / {pages}</span>
                        <button type="button" className={pagerBtn} disabled={page >= pages} onClick={() => setRepPage(page + 1)} aria-label="Next page">›</button>
                      </div>
                    </div>
                    {filtered.length === 0 ? (
                      <div className="text-center py-4 text-sm text-gray-500 dark:text-slate-400">No repayments in these dates.</div>
                    ) : (
                <div className="bg-white dark:bg-slate-800 overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className={TABLE_HEAD_ROW}>
                        {repTh('Date', 'date', dayOf, { className: repHead })}
                        {repTh('Method', 'method', (r) => r?.payment_method, { className: repHead })}
                        {repTh('Reference', 'reference', (r) => r?.payment_reference, { className: repHead })}
                        {repTh('Amount (N$)', 'amount', (r) => Number(r?.amount ?? r?.amount_paid ?? 0), { className: repHead, align: 'right' })}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, idx) => (
                        <tr
                          key={r?.id || idx}
                          {...(r?.id && canViewRepayments ? clickableProps(() => openRepayment(r), 'Open repayment details') : {})}
                          title={r?.id && canViewRepayments ? 'Open repayment details' : undefined}
                          className={`${TABLE_ROW_LINE} ${r?.id && canViewRepayments ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700/60 focus:outline-none focus-visible:bg-blue-50' : ''}`}
                        >
                          <td className="px-3 py-2 whitespace-nowrap">{formatDateDMY(r?.date || r?.payment_date)}</td>
                          <td className="px-3 py-2">{r?.payment_method || '-'}</td>
                          <td className={`px-3 py-2 break-words ${r?.payment_reference ? 'font-mono' : 'text-gray-400 dark:text-slate-500'}`} title={r?.payment_reference || ''}>
                            {r?.payment_reference || 'None'}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <RepaymentLink repayment={r?.id ? r : null}>{toCurrency(Number(r?.amount ?? r?.amount_paid ?? 0))}</RepaymentLink>
                          </td>
                        </tr>
                      ))}
                      <tr className={`${TABLE_ROW_LINE} bg-blue-50 dark:bg-blue-900/20 font-semibold`}>
                        <td className="px-3 py-2" colSpan={3}>
                          {filtering ? `Total in these dates (${filtered.length})` : `Total Paid (${all.length})`}
                        </td>
                        <td className="px-3 py-2 text-right">{toCurrency(Number(filtering ? shownTotal : totalPaid))}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Where an opened document shows: the whole row (hidden when empty). */}
          <div ref={setDocViewerEl} className="order-3 xl:col-span-2 empty:hidden" />

          {/* Loan Details Section */}
          {/* The same section, groups and order as editing the loan (LoanEditForm). */}
          <LoanSection Icon={CashIcon} title="Loan Details" tone="yellow" className="order-first xl:col-span-2 mt-6">
            {(q || suggested.error || termsMessage) && (
              <div className="mb-4 border border-indigo-200 dark:border-indigo-700/60 bg-indigo-50/60 dark:bg-slate-900 px-3 py-2">
                {q && !termsMessage && (
                  <>
                    <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
                      Suggested amount filled in: {toCurrency(q.principal_amount)} over {q.duration_months} {q.duration_months === 1 ? 'month' : 'months'}
                    </p>
                    <p className="text-xs text-indigo-800 dark:text-indigo-200">
                      The loan asks for {toCurrency(loan?.principal_amount)} over {loan?.duration_months} {Number(loan?.duration_months) === 1 ? 'month' : 'months'}, more than the client qualifies for.
                      Every figure below is worked out for the suggested amount (the loan's own is struck through next to it).
                      Update the loan to keep them: the calculation runs again and the client signs the new agreement.
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button type="button" onClick={updateToSuggested} disabled={updatingTerms}
                        className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-60">
                        {updatingTerms ? 'Updating…' : 'Update loan'}
                      </button>
                      <button type="button" onClick={() => setKeepCurrentTerms(true)} disabled={updatingTerms}
                        className="px-4 py-1.5 rounded-md border border-gray-300 dark:border-slate-600 text-sm text-gray-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800">
                        Keep {toCurrency(loan?.principal_amount)}
                      </button>
                    </div>
                  </>
                )}
                {suggested.error && !q && <p className="text-xs text-rose-700 dark:text-rose-300">{suggested.error}</p>}
                {termsMessage && <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400">{termsMessage}</p>}
                {termsError && <p className="mt-1 text-xs font-medium text-rose-700 dark:text-rose-400">{termsError}</p>}
              </div>
            )}
            {/* Figures across the row; the expenses and penalty history side by side under them. */}
            <div className={LOAN_GROUPS_GRID}>
              <FieldGroup title="Loan" tone="blue" wide>
                <DisplayField label="Loan Code">{loan?.loan_code || loan?.loanCode || '-'}</DisplayField>
                <DisplayField label="Loan Reason">{loan?.loan_reason_name || loan?.loan_reason || '-'}</DisplayField>
                <DisplayField label="Start Date">{was(dmyOf(q?.start_date || loan?.start_date), dmyOf(loan?.start_date))}</DisplayField>
                <DisplayField label="Due Date">{was(dmyOf(q?.due_date || loan?.due_date), dmyOf(loan?.due_date))}</DisplayField>
                <DisplayField label="Duration (months)">{was(q?.duration_months || loan?.duration_months || '-', loan?.duration_months || '-')}</DisplayField>
                <DisplayField label="Interest Rate">{loan?.interest_rate ? `${loan.interest_rate}%` : '-'}</DisplayField>
              </FieldGroup>

              <FieldGroup title="Amounts" tone="emerald">
                <DisplayField label="Principal Amount" tone={q ? 'strong' : null}>{q ? was(toCurrency(q.principal_amount), toCurrency(loan?.principal_amount)) : (loan?.principal_amount ? toCurrency(loan.principal_amount) : '-')}</DisplayField>
                <DisplayField label="Principal + Interest">{q ? was(toCurrency(q.principal_amount_plus_interest), toCurrency(loan?.principal_amount_plus_interest)) : (loan?.principal_amount_plus_interest ? toCurrency(loan.principal_amount_plus_interest) : '-')}</DisplayField>
                <DisplayField label="Base Loan Obligation" tone="strong">{q ? was(toCurrency(q.total_repayable), toCurrency(baseLoanObligation)) : toCurrency(baseLoanObligation)}</DisplayField>
              </FieldGroup>

              <FieldGroup title="Additional Expenses" tone="amber">
                {loadingAdditionalExpenses ? (
                  <DisplayField label="Additional Expenses">Loading…</DisplayField>
                ) : additionalExpenses.map((expense) => {
                  const quoted = q ? (q.expenses || []).find((x) => x.expense_code === expense.expense_code) : null;
                  const amount = toCurrency(Number(expense.calculated_amount || 0));
                  return (
                    <DisplayField key={expense.id || expense.expense_code} label={expense.expense_name || expense.expense_code}>
                      {quoted ? was(toCurrency(Number(quoted.calculated_amount || 0)), amount) : amount}
                    </DisplayField>
                  );
                })}
                <DisplayField label="Total Additional Expenses">{q ? was(toCurrency(q.expenses_total), toCurrency(additionalExpensesTotal)) : toCurrency(additionalExpensesTotal)}</DisplayField>
              </FieldGroup>

              <RepaymentGroup
                loan={loan}
                monthly={q ? was(toCurrency(q.monthly_payment), toCurrency(loan?.monthly_payment)) : (loan?.monthly_payment ? toCurrency(loan.monthly_payment) : '-')}
                totalDue={Number.isFinite(effectiveTotalDue) ? toCurrency(effectiveTotalDue) : '-'}
              />

              <PayoutGroup loan={loan} />
              {['Bad Debt', 'Written off'].includes(loan?.status) && (
                <FieldGroup title="Bad debt" tone="slate" wide>
                  <DisplayField label="Bad debt" tone="alert">
                    {badDebtNote ? `Since ${formatDateDMY(badDebtNote.created_at)}` : (loadingStatusNotes ? '…' : 'Yes')}
                  </DisplayField>
                  {badDebtNote?.note_text && <DisplayField label="Note" className="col-span-full">{badDebtNote.note_text}</DisplayField>}
                </FieldGroup>
              )}


              {showPenalties && (
              <div className="col-span-full">
                <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">Penalty Interest</div>
                {loadingInvoices ? (
                  <div className="text-sm text-gray-600 dark:text-slate-400">Loading penalty interest...</div>
                ) : (
                  <PenaltyBreakdown loanId={loan?.id} breakdown={penaltyBreakdown} showEmptySummary />
                )}
              </div>
              )}

              {/* Debit order collections (staff; the component hides itself for clients). */}
              {loan?.id && ['Approved', 'Outstanding', 'Bad Debt'].includes(loan?.status) && (
              <div className="col-span-full">
                <div className="text-xs text-gray-500 dark:text-slate-400 mb-2">Debit Order Collections</div>
                <DebitOrderCollections loanId={loan.id} />
              </div>
              )}
            </div>
          </LoanSection>

          {/* Rejection Reason Section */}
          {loan?.status === 'Rejected' && (
            <div className="order-7 mt-6 bg-red-50/50 dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-red-100 dark:border-slate-700">
              <div className="flex items-center mb-3 pb-2 border-b border-red-100 dark:border-slate-700">
                <WarningIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <h3 className="text-base font-medium text-red-800 dark:text-red-200">Rejection Reason</h3>
              </div>
              <div className="text-red-700 dark:text-red-300">
                {loan?.rejection_reason ? loan.rejection_reason : 'No reason provided.'}
              </div>
            </div>
          )}


          {/* Cancellation Reason Section */}
          {loan?.status === 'Cancelled' && (
            <div className="order-7 mt-6 bg-orange-50/50 dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-orange-100 dark:border-slate-700">
              <div className="flex items-center mb-3 pb-2 border-b border-orange-100 dark:border-slate-700">
                <WarningIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" />
                <h3 className="text-base font-medium text-orange-800 dark:text-orange-200">Cancellation Reason</h3>
              </div>
              {loadingNotes ? (
                <div className="text-sm text-gray-600 dark:text-slate-300">Loading cancellation note...</div>
              ) : cancellationNotes && cancellationNotes.length > 0 ? (
                <div className="space-y-3">
                  {cancellationNotes.map(n => (
                    <div key={n.id} className="text-orange-700 dark:text-orange-200 whitespace-pre-line">
                      {n.note_text || 'No details provided.'}
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">{n.created_at ? formatDateTimeDMY(n.created_at) : ''}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-orange-700 dark:text-orange-200">No reason provided.</div>
              )}
            </div>
          )}

          {/* Write-off notes (bad debt itself shows in the loan details). */}
          {loan?.status === 'Written off' && (statusNotes || []).some((n) => n.note_type !== 'BadDebt') && (
            <div className="order-7 mt-6 bg-red-50/50 dark:bg-slate-800 rounded-lg p-5 shadow-sm border border-red-100 dark:border-slate-700">
              <div className="flex items-center mb-3 pb-2 border-b border-red-100 dark:border-slate-700">
                <WarningIcon className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
                <h3 className="text-base font-medium text-red-800 dark:text-red-200">
                  Write-off Notes
                </h3>
              </div>
              {loadingStatusNotes ? (
                <div className="text-sm text-gray-600 dark:text-slate-300">Loading notes...</div>
              ) : statusNotes && statusNotes.length > 0 ? (
                <div className="space-y-3">
                  {statusNotes.filter((n) => n.note_type !== 'BadDebt').map(n => (
                    <div key={n.id} className="text-red-800 dark:text-red-200 whitespace-pre-line">
                      <div className="text-xs text-gray-600 dark:text-slate-300 mb-1">{n.note_type || ''}</div>
                      {n.note_text || 'No details provided.'}
                      <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">{n.created_at ? formatDateTimeDMY(n.created_at) : ''}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-red-800 dark:text-red-200">No notes found.</div>
              )}
            </div>
          )}
      </div>
      {/* Staff signing (Awaiting Staff): the contract, for the lender and
          witness signatures, below the loan's details. */}
      {staffSigning && <div className="mt-6 max-w-full overflow-x-auto">{staffSigning}</div>}
      {/* Everything that has happened to this loan: always last (staff only). */}
      {loan?.id && canSeeAuditTrail && (
        <EntityAuditTrail
          scope="loan"
          id={loan.id}
          title={`Audit trail — ${loan?.loan_code || `Loan #${loan.id}`}`}
          className="mt-6"
        />
      )}
      {acceptOpen && (
        <ActionDialog
          title="Accept for signing"
          onClose={() => { setAcceptOpen(false); setAcceptError(''); }}
          busy={dialogSaving}
          confirmText={dialogSaving ? 'Sending…' : 'Accept and send to client'}
          confirmClass="bg-green-600 hover:bg-green-700 text-white"
          onConfirm={confirmAccept}
          error={acceptError}
        >
          <p className="text-sm text-gray-700 dark:text-slate-200 mb-3">
            The verification calculator declined this application and the client asked for review.
            Accepting it sends the loan agreement to the client to sign, at the amount and term below.
            Approval still follows once it is signed.
          </p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <label className="block">
              <span className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Amount (N$)</span>
              <input type="number" min="1" step="0.01" className="w-full" value={acceptForm.amount}
                onChange={(e) => { setAcceptForm((f) => ({ ...f, amount: e.target.value })); setAcceptError(''); }} />
            </label>
            <label className="block">
              <span className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Term (months)</span>
              <input type="number" min="1" step="1" className="w-full" value={acceptForm.months}
                onChange={(e) => { setAcceptForm((f) => ({ ...f, months: e.target.value })); setAcceptError(''); }} />
            </label>
          </div>
          {calculator?.suggestion && (
            <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
              The calculator's suggestion: {toCurrency(calculator.suggestion.amount)} over {calculator.suggestion.months} {calculator.suggestion.months === 1 ? 'month' : 'months'}.
            </p>
          )}
          <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Note to the client (required)</label>
          <textarea
            className="w-full focus:ring-green-500/30 focus:border-green-500 h-20 resize-none"
            value={acceptForm.note}
            onChange={(e) => { setAcceptForm((f) => ({ ...f, note: e.target.value })); setAcceptError(''); }}
          />
          <RiskReasonField value={riskReason} onChange={setRiskReason} />
        </ActionDialog>
      )}
      {showNoteInput?.[loan?.id] && (
        <ActionDialog
          title="Resend application form"
          onClose={closeNote}
          busy={dialogSaving}
          confirmText={dialogSaving ? 'Sending…' : 'Send to client'}
          confirmClass="bg-yellow-600 hover:bg-yellow-700 text-white"
          onConfirm={sendNote}
          error={noteError}
        >
          <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Note to the client (required)</label>
          <textarea
            className="w-full focus:ring-yellow-500/30 focus:border-yellow-500 h-24 resize-none"
            value={moreDetailsNotes?.[loan?.id] || ''}
            onChange={e => { setMoreDetailsNotes(prev => ({ ...prev, [loan?.id]: e.target.value })); setNoteError(''); }}
            placeholder="Describe what needs to be corrected or updated before resubmission..."
            autoFocus
          />
          {calculatorDeclined && <RiskReasonField value={riskReason} onChange={setRiskReason} />}
        </ActionDialog>
      )}
      {approveReasonOpen && (
        <ActionDialog
          title="Approve loan"
          onClose={() => { setApproveReasonOpen(false); setApproveReasonError(''); }}
          busy={dialogSaving}
          confirmText={dialogSaving ? 'Approving…' : 'Yes, approve'}
          confirmClass="bg-green-600 hover:bg-green-700 text-white"
          onConfirm={confirmApproveReason}
          error={approveReasonError}
        >
          <p className="text-sm text-gray-700 dark:text-slate-200 mb-3">
            {approveHold
              ? approveHold
              : <>The affordability check's decision on this loan is <strong>{affordabilityDecision === 'Declined' ? 'Declined' : 'Manual review'}</strong>.</>}
            {' '}Approve it anyway? Your reason is kept with the check, under your name. The statutory maximum still applies.
          </p>
          <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Reason for approving (required)</label>
          <textarea
            className="w-full resize-y h-24"
            value={approveReason}
            onChange={(e) => { setApproveReason(e.target.value); setApproveReasonError(''); }}
            placeholder="Why this loan is approved…"
            autoFocus
          />
        </ActionDialog>
      )}
      {rejectInputOpen && (
        <ActionDialog
          title="Reject loan"
          onClose={closeReject}
          busy={dialogSaving}
          confirmText={dialogSaving ? 'Rejecting…' : 'Yes, reject'}
          confirmClass="bg-red-600 hover:bg-red-700 text-white"
          onConfirm={confirmReject}
          error={rejectError}
        >
          <p className="text-sm text-gray-700 dark:text-slate-200 mb-3">
            {calculatorRejection
              ? 'Reject this loan? The client receives this message, written from the calculator. You can change it.'
              : 'Reject this loan? Write the reason the client receives.'}
          </p>
          <label className="block text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1.5 uppercase tracking-wide">Message to the client (required)</label>
          <textarea
            className={`w-full resize-y ${calculatorRejection ? 'h-64' : 'h-24'}`}
            value={rejectReason}
            onChange={e => { setRejectReason(e.target.value); setRejectError(''); }}
            placeholder="Enter reason for rejection..."
            autoFocus
          />
        </ActionDialog>
      )}
    </Frame>
    {/* After the details, so it opens over them. */}
    {editing && (
      <LoanEditForm
        loan={{ ...loan, clientName: loan?.clientName || loan?.client_name || [loan?.first_name, loan?.last_name].filter(Boolean).join(' ') }}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onClose();
          try { onEdited(); } catch {}
        }}
      />
    )}
    </>
  );
};

export default LoanDetails;